import { yupResolver } from '@hookform/resolvers/yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  HStack,
  SimpleGrid,
  Select,
  Input,
  InputGroup,
  InputRightAddon,
} from '@wegroup/design-system';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { FormOption } from '../../../../../../../types/Form';
import { UnitObject } from '../../../../types/CompanyScanTypes';

interface Props {
  unit?: UnitObject;
  isOpen: boolean;
  options: FormOption[];
  onClose: () => void;
  addUnit: (unit: UnitObject) => void;
  editUnit: (unit: UnitObject) => void;
}

const unitsValidationSchema = yup.object().shape({
  unit: yup.string().required('Required'),
  value: yup.number().typeError('Required'),
});

const NewUnitModal: React.FC<React.PropsWithChildren<Props>> = ({
  unit,
  options,
  addUnit,
  editUnit,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  const modalGoal = unit ? 'EDIT' : 'ADD';

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<UnitObject>({
    resolver: yupResolver(unitsValidationSchema),
    defaultValues: {
      unit: unit?.unit,
      value: unit?.value,
    },
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  const handleOnSubmit = handleSubmit((newUnits: UnitObject) => {
    if (unit) editUnit(newUnits);
    else addUnit(newUnits);
    handleOnClose();
  });

  return (
    <Modal isOpen={isOpen} onClose={handleOnClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t(`FLOWS_COMPANY-SCAN_UNITS-INPUT_${modalGoal}-UNIT`)}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody as={SimpleGrid} columns={2} gap="4">
          <FormControl isInvalid={!!errors.value}>
            <FormLabel>{t('WAGE_BILL')}</FormLabel>

            <InputGroup>
              <Input
                {...register('value')}
                placeholder={t('WAGE_BILL')}
                type="number"
              />
              <InputRightAddon bg="white" color="black" fontWeight="400">
                €
              </InputRightAddon>
            </InputGroup>
            {errors.value?.message && (
              <FormErrorMessage>{t(errors.value.message)}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.unit}>
            <FormLabel>{t('CATEGORY')}</FormLabel>
            <Select {...register('unit')} isDisabled={options.length === 0}>
              {options.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            {errors.unit?.message && (
              <FormErrorMessage>{t(errors.unit.message)}</FormErrorMessage>
            )}
          </FormControl>
        </ModalBody>

        <ModalFooter as={HStack} justify="flex-end" spacing="4">
          <Button variant="ghost" mr={3} onClick={handleOnClose}>
            {t('CLOSE')}
          </Button>
          <Button onClick={handleOnSubmit}>
            {modalGoal ? t('SAVE') : t('ADD')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NewUnitModal;
